import React, { createContext, useContext, useState, useCallback } from 'react'
import { Modal } from '../../components/common/Modal/Modal'

interface ModalOptions {
  title?: string
  type?: 'info' | 'error' | 'success' | 'warning' | 'confirm'
  confirmText?: string
  cancelText?: string
  onConfirm?: () => void | Promise<void>
}

interface ModalContextType {
  showModal: (message: string, options?: ModalOptions) => void
  showError: (message: string, title?: string) => void
  showSuccess: (message: string, title?: string) => void
  showWarning: (message: string, title?: string) => void
  showConfirm: (message: string, onConfirm: () => void | Promise<void>, title?: string) => Promise<boolean>
}

const ModalContext = createContext<ModalContextType | undefined>(undefined)

export const useModal = () => {
  const context = useContext(ModalContext)
  if (!context) {
    throw new Error('useModal must be used within a ModalProvider')
  }
  return context
}

interface ModalState {
  isOpen: boolean
  message: string
  title: string
  type: 'info' | 'error' | 'success' | 'warning' | 'confirm'
  confirmText: string
  cancelText: string
  onConfirm?: () => void | Promise<void>
}

export const ModalProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [modalState, setModalState] = useState<ModalState>({
    isOpen: false,
    message: '',
    title: 'Notice',
    type: 'info',
    confirmText: 'OK',
    cancelText: 'Cancel',
  })

  const closeModal = useCallback(() => {
    setModalState(prev => ({ ...prev, isOpen: false }))
  }, [])

  const showModal = useCallback((message: string, options?: ModalOptions) => {
    setModalState({
      isOpen: true,
      message,
      title: options?.title || 'Notice',
      type: options?.type || 'info',
      confirmText: options?.confirmText || 'OK',
      cancelText: options?.cancelText || 'Cancel',
      onConfirm: options?.onConfirm,
    })
  }, [])

  const showError = useCallback((message: string, title?: string) => {
    showModal(message, {
      title: title || 'Error',
      type: 'error',
    })
  }, [showModal])

  const showSuccess = useCallback((message: string, title?: string) => {
    showModal(message, {
      title: title || 'Success',
      type: 'success',
    })
  }, [showModal])

  const showWarning = useCallback((message: string, title?: string) => {
    showModal(message, {
      title: title || 'Warning',
      type: 'warning',
    })
  }, [showModal])

  const showConfirm = useCallback((
    message: string,
    onConfirm: () => void | Promise<void>,
    title?: string
  ): Promise<boolean> => {
    return new Promise((resolve) => {
      setModalState({
        isOpen: true,
        message,
        title: title || 'Confirm',
        type: 'confirm',
        confirmText: 'Confirm',
        cancelText: 'Cancel',
        onConfirm: async () => {
          await onConfirm()
          resolve(true)
        },
      })
    })
  }, [])

  const handleConfirm = async () => {
    if (modalState.onConfirm) {
      await modalState.onConfirm()
    }
    closeModal()
  }

  return (
    <ModalContext.Provider
      value={{
        showModal,
        showError,
        showSuccess,
        showWarning,
        showConfirm,
      }}
    >
      {children}
      <Modal
        isOpen={modalState.isOpen}
        onClose={closeModal}
        onConfirm={modalState.type === 'confirm' ? handleConfirm : undefined}
        title={modalState.title}
        message={modalState.message}
        type={modalState.type}
        confirmText={modalState.confirmText}
        cancelText={modalState.cancelText}
      />
    </ModalContext.Provider>
  )
}